import React, { useEffect, useState } from "react";
import {
  Box,
  IconButton,
  Popover,
  VStack,
  StackSeparator,
  ButtonGroup,
  Text,
  HStack,
  useDisclosure,
  Spinner,
  Button,
} from "@chakra-ui/react";
import { NotificationBadge } from "./NotificationBadge";
import { t } from "i18next";
import { NotificationItem } from "./NotificationItem";
import { NotificationSettingsModal } from "./NotificationSettingsModal";
import { observer } from "mobx-react";
import { NotificationApi } from "../services/NotificationApi";
import { useStores } from "../../shared/stores/RootStore";
import { BellIcon, SettingsIcon, ViewIcon } from "../../shared/views/icons";
import { CustomPopover } from "../../shared/views/CustomPopover";
import {
  newNotificationToaster,
  NewNotificationToaster,
} from "./NewNotificationToaster";

export const NotificationsBase: React.FC = () => {
  const { notificationStore } = useStores();
  const [loading, setLoading] = useState(false);
  const settingsModal = useDisclosure();

  useEffect(() => {
    const timerPromise = (async () => {
      await notificationStore.fetchCounter();
      return setInterval(async () => {
        if (!document.hidden) {
          // only when browser tab is active
          await notificationStore.fetchCounter((amount) =>
            toastNewNotifications(amount)
          );
        }
      }, 30 * 1000);
    })();

    return () => {
      timerPromise.then((timer) => {
        clearInterval(timer);
      });
    };
    // eslint-disable-next-line
  }, []);

  const toastNewNotifications = (amount: number) => {
    NotificationApi.list(amount).then(({ data }) => {
      data.data.forEach((n) => {
        newNotificationToaster.create({
          closable: true,
          description: n as any,
        });
      });
    });
  };

  const handleScroll = (e: React.UIEvent<HTMLElement>) => {
    const target = e.currentTarget as HTMLElement;
    const bottom =
      target.scrollHeight - target.scrollTop <= target.clientHeight;
    if (bottom) {
      setLoading(true);
      notificationStore.fetchNotifications().finally(() => setLoading(false));
    }
  };

  return (
    <>
      <CustomPopover
        onOpen={() => {
          setLoading(true);
          notificationStore.resetNotifications();
          notificationStore.fetchNotifications().finally(() => {
            setLoading(false);
          });
        }}
        w={"md"}
        trigger={
          <IconButton variant="ghost">
            <Box position="relative">
              <BellIcon fontSize="2xl" />
              <NotificationBadge value={notificationStore.counter?.unread} />
            </Box>
          </IconButton>
        }
      >
        <Popover.Header>
          <HStack>
            <Text>{t("notification:notifications")}</Text>
            <IconButton
              size="sm"
              onClick={settingsModal.onOpen}
              variant="ghost"
              colorPalette="primary"
              aria-label="settings"
              my={-2}
            >
              <SettingsIcon />
            </IconButton>
          </HStack>
        </Popover.Header>
        <Popover.Body maxH="lg" overflow="auto" onScroll={handleScroll}>
          {notificationStore.notifications?.length ? (
            <VStack separator={<StackSeparator />}>
              <ButtonGroup size="xs" colorPalette="primary" variant="ghost">
                <Button onClick={() => notificationStore.readAll()}>
                  <ViewIcon /> {t("notification:markAllAsRead")}
                </Button>
              </ButtonGroup>
              {notificationStore.notifications?.map((n, i) => (
                <NotificationItem notification={n} key={i} />
              ))}
              {loading && (
                <Spinner borderWidth="3px" size="md" color="primary.500" />
              )}
            </VStack>
          ) : loading ? (
            <HStack justifyContent={"center"}>
              <Spinner color="primary.500" />
            </HStack>
          ) : (
            <Text fontSize="sm">{t("notification:empty")}</Text>
          )}
        </Popover.Body>
      </CustomPopover>

      {settingsModal.open && (
        <NotificationSettingsModal onClose={settingsModal.onClose} />
      )}

      <NewNotificationToaster />
    </>
  );
};

export const Notifications = observer(NotificationsBase);
